/**
 * ChecklistApplyTemplateDialog - Diálogo para aplicar um Template de Checklist a uma tarefa
 *
 * Fluxo:
 *   1. Lista os templates ativos visíveis para o usuário
 *   2. Ao selecionar, mostra preview (grupos + itens) via get_task_checklist_template
 *   3. Usuário escolhe modo Anexar (append) ou Substituir (replace)
 *   4. Executa apply_task_checklist_template
 *
 * @version 1.2.0
 * RELEASE: 2026-08-18 v1.2.0 - Usabilidade: modal maior, busca de templates com
 *          normalização de acentos, nomes completos (sem truncar) com tooltip e
 *          destaque do termo pesquisado, preview em 2 colunas.
 * RELEASE: 2026-07-07 v1.1.0 - Shell premium (split-pane) igual ao editor de anotações.
 * RELEASE: 2026-07-07 v1.0.0 - Versão inicial da Fase 3.
 */
import { useState, useMemo } from 'react';
import {
  Loader2,
  LayoutList,
  CheckCircle2,
  ListChecks,
  AlertTriangle,
  Eye,
  Inbox,
  Search,
  X,
  SearchX,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { FormSectionCard } from '@/components/crm/deals/components/FormSectionCard';
import {
  useTaskChecklistTemplatesList,
  useTaskChecklistTemplateDetail,
  useApplyTaskChecklistTemplate,
} from '@/hooks/useTaskChecklistTemplates';

/**
 * Normaliza texto para busca: remove acentos, baixa caixa e colapsa espaços.
 * Assim "ativacao" encontra "[Ativação]".
 */
const normalize = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

/**
 * Destaca o trecho pesquisado dentro do nome do template.
 * A comparação é feita sobre a versão normalizada, mas o texto exibido
 * preserva os acentos originais.
 */
function HighlightedText({ text, term }: { text: string; term: string }) {
  const normalizedTerm = normalize(term);
  if (!normalizedTerm) return <>{text}</>;

  const normalizedText = normalize(text);
  const start = normalizedText.indexOf(normalizedTerm);
  if (start < 0) return <>{text}</>;

  const end = start + normalizedTerm.length;
  return (
    <>
      {text.slice(0, start)}
      <mark className="bg-primary/20 text-foreground rounded-[2px] px-0.5">
        {text.slice(start, end)}
      </mark>
      {text.slice(end)}
    </>
  );
}


interface ChecklistApplyTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string;
  /** Indica se há checklist atual (afeta aviso do modo Substituir) */
  hasExistingChecklist?: boolean;
}

export function ChecklistApplyTemplateDialog({
  open,
  onOpenChange,
  taskId,
  hasExistingChecklist = false,
}: ChecklistApplyTemplateDialogProps) {
  // Somente templates ativos podem ser aplicados
  const { data: templates, isLoading: loadingList } = useTaskChecklistTemplatesList(true);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mode, setMode] = useState<'append' | 'replace'>('append');
  // v1.2.0 — busca client-side sobre a lista já carregada
  const [search, setSearch] = useState('');

  const { data: detail, isLoading: loadingDetail } = useTaskChecklistTemplateDetail(
    selectedId,
    open,
  );

  const applyMutation = useApplyTaskChecklistTemplate();

  const totalItems = useMemo(
    () => (detail?.groups ?? []).reduce((s, g) => s + g.items.length, 0),
    [detail],
  );

  /** Templates filtrados por nome + descrição (sem acento / case-insensitive) */
  const filteredTemplates = useMemo(() => {
    const list = templates ?? [];
    const term = normalize(search);
    if (!term) return list;
    return list.filter(
      (t) =>
        normalize(t.name).includes(term) ||
        normalize(t.description ?? '').includes(term),
    );
  }, [templates, search]);

  // Indica que o template selecionado não está visível na lista filtrada
  const selectedHiddenByFilter =
    !!selectedId && !filteredTemplates.some((t) => t.id === selectedId);

  const reset = () => {
    setSelectedId(null);
    setMode('append');
    setSearch('');
  };


  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  const handleApply = async () => {
    if (!selectedId) return;
    await applyMutation.mutateAsync({ taskId, templateId: selectedId, mode });
    handleClose(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-6xl p-0 gap-0 overflow-hidden max-h-[90vh] h-[90dvh] sm:h-[85dvh] flex flex-col"

        hideCloseButton={false}
      >
        {/* ===== HEADER PREMIUM ===== */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent">
          <DialogHeader>
            <div className="flex items-center justify-between gap-2">
              <DialogTitle className="flex items-center gap-2 text-base">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                  <ListChecks className="h-3.5 w-3.5 text-primary" />
                </div>
                Aplicar template de checklist
              </DialogTitle>
              {detail && (
                <Badge variant="secondary" className="gap-1">
                  {detail.groups.length} listas · {totalItems} itens
                </Badge>
              )}
            </div>
            <DialogDescription className="text-xs text-muted-foreground">
              Selecione um template para adicionar as listas de verificação nesta tarefa.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* ===== CORPO SPLIT-PANE ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-[360px,1fr] min-h-0 flex-1">
          {/* Painel esquerdo — lista de templates */}
          <div className="border-b lg:border-b-0 lg:border-r flex flex-col min-h-0 bg-muted/10">
            <div className="flex items-center gap-2 px-4 py-3 border-b bg-background/80 backdrop-blur-sm flex-shrink-0">
              <LayoutList className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-medium">Templates</span>
              {templates && templates.length > 0 && (
                <Badge variant="outline" className="ml-auto text-[10px] h-5">
                  {/* Mostra "filtrados/total" enquanto há busca ativa */}
                  {search.trim()
                    ? `${filteredTemplates.length}/${templates.length}`
                    : templates.length}
                </Badge>
              )}
            </div>

            {/* v1.2.0 — Campo de busca fixo (não rola com a lista) */}
            <div className="px-3 py-2 border-b bg-background/60 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value.slice(0, 100))}
                  placeholder="Buscar template…"
                  className="h-8 pl-8 pr-8 text-xs"
                  aria-label="Buscar template de checklist"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    aria-label="Limpar busca"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {selectedHiddenByFilter && (
                <p className="mt-1.5 text-[11px] text-muted-foreground">
                  O template selecionado está fora do filtro atual.
                </p>
              )}
            </div>

            <ScrollArea className="flex-1 min-h-0">
              <div className="p-2">
                {loadingList ? (
                  <div className="flex items-center gap-2 px-2 py-3 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Carregando…
                  </div>
                ) : !templates || templates.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-8 text-center">
                    <Inbox className="h-6 w-6 text-muted-foreground/60" />
                    <p className="text-xs text-muted-foreground">
                      Nenhum template disponível.
                    </p>
                  </div>
                ) : filteredTemplates.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 py-8 text-center">
                    <SearchX className="h-6 w-6 text-muted-foreground/60" />
                    <p className="text-xs text-muted-foreground">
                      Nenhum template encontrado para “{search.trim()}”.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-3 text-xs"
                      onClick={() => setSearch('')}
                    >
                      Limpar busca
                    </Button>
                  </div>
                ) : (
                  <TooltipProvider delayDuration={400}>
                    <ul className="space-y-1">
                      {filteredTemplates.map((t) => {
                        const selected = selectedId === t.id;
                        return (
                          <li key={t.id}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  onClick={() => setSelectedId(t.id)}
                                  className={cn(
                                    'w-full rounded-md border px-3 py-2 text-left transition-colors',
                                    selected
                                      ? 'border-primary bg-primary/10'
                                      : 'border-transparent hover:bg-accent/40',
                                  )}
                                >
                                  <div className="flex items-start gap-2">
                                    <LayoutList className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                                    {/* Nome completo: quebra em até 2 linhas em vez de cortar */}
                                    <span className="text-sm font-medium leading-snug line-clamp-2 break-words flex-1">
                                      <HighlightedText text={t.name} term={search} />
                                    </span>
                                    {selected && (
                                      <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
                                    )}
                                  </div>
                                  <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground pl-5">
                                    <span>{t.group_count} listas</span>
                                    <span>·</span>
                                    <span>{t.item_count} itens</span>
                                  </div>
                                  {t.description && (
                                    <p className="mt-0.5 text-[11px] text-muted-foreground/80 truncate pl-5">
                                      {t.description}
                                    </p>
                                  )}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-xs">
                                <p className="text-xs">{t.name}</p>
                                {t.description && (
                                  <p className="mt-1 text-[11px] text-muted-foreground">
                                    {t.description}
                                  </p>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </li>
                        );
                      })}
                    </ul>
                  </TooltipProvider>
                )}
              </div>

            </ScrollArea>
          </div>

          {/* Painel direito — preview + modo de aplicação (altura flexível) */}
          <ScrollArea className="min-h-0 h-full">

            <div className="p-5 space-y-4">
              {/* Preview */}
              <FormSectionCard title="Preview" icon={Eye}>
                {!selectedId ? (
                  <div className="flex flex-col items-center gap-2 py-8 text-center">
                    <Eye className="h-6 w-6 text-muted-foreground/60" />
                    <p className="text-sm text-muted-foreground">
                      Selecione um template à esquerda para pré-visualizar.
                    </p>
                  </div>
                ) : loadingDetail || !detail ? (
                  <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Carregando template…
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-semibold">{detail.name}</h4>
                      {detail.description && (
                        <p className="mt-0.5 text-xs text-muted-foreground">{detail.description}</p>
                      )}
                    </div>
                    {/* Grid de 2 colunas em telas grandes para aproveitar a largura */}
                    <ul className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                      {detail.groups.map((g) => (
                        <li key={g.id} className="rounded-md border bg-background p-3">
                          <p className="text-xs font-semibold text-foreground break-words">{g.title}</p>
                          <ul className="mt-1 space-y-0.5 pl-3">
                            {g.items.map((it) => (
                              <li
                                key={it.id}
                                className="text-xs text-muted-foreground list-disc list-inside break-words"
                              >
                                {it.content}
                              </li>
                            ))}

                            {g.items.length === 0 && (
                              <li className="text-[11px] italic text-muted-foreground">
                                (sem itens)
                              </li>
                            )}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </FormSectionCard>

              {/* Modo de aplicação */}
              <FormSectionCard title="Modo de aplicação" icon={LayoutList}>
                <RadioGroup
                  value={mode}
                  onValueChange={(v) => setMode(v as 'append' | 'replace')}
                  className="space-y-2"
                >
                  <label className="flex items-start gap-2 cursor-pointer rounded-md border bg-background p-3 hover:bg-accent/40 transition-colors">
                    <RadioGroupItem value="append" id="apply-mode-append" className="mt-0.5" />
                    <Label htmlFor="apply-mode-append" className="cursor-pointer flex-1">
                      <span className="text-sm font-medium">Anexar</span>
                      <p className="text-xs text-muted-foreground">
                        Adiciona as listas do template ao final do checklist atual.
                      </p>
                    </Label>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer rounded-md border bg-background p-3 hover:bg-accent/40 transition-colors">
                    <RadioGroupItem value="replace" id="apply-mode-replace" className="mt-0.5" />
                    <Label htmlFor="apply-mode-replace" className="cursor-pointer flex-1">
                      <span className="text-sm font-medium">Substituir</span>
                      <p className="text-xs text-muted-foreground">
                        Remove o checklist atual antes de aplicar o template.
                      </p>
                    </Label>
                  </label>
                </RadioGroup>
                {mode === 'replace' && hasExistingChecklist && (
                  <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-2">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 text-destructive shrink-0" />
                    <p className="text-[11px] text-destructive">
                      Esta ação apagará todas as listas de verificação atuais desta tarefa.
                    </p>
                  </div>
                )}
              </FormSectionCard>
            </div>
          </ScrollArea>
        </div>

        {/* ===== FOOTER ===== */}
        <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-3 border-t bg-muted/10">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-3"
            onClick={() => handleClose(false)}
            disabled={applyMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            className="h-7 px-3"
            onClick={handleApply}
            disabled={!selectedId || applyMutation.isPending}
          >
            {applyMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            {applyMutation.isPending ? 'Aplicando…' : 'Aplicar template'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
